import { CSSObject } from '@emotion/react';
import { ResponsiveStyleValue, createResponsiveStyles, useHoneycombTheme } from '../../theming';
import { CardOrientation } from '../../theming/types/components/Card.types';

export interface CardSectionStylesParams {
    orientation: ResponsiveStyleValue<CardOrientation>;
}

type CardStyleClasses = 'root' | 'surface' | 'sections';

export function useStyles({ orientation }: CardSectionStylesParams): {
    styles: { [key in CardStyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const cardTheme = theme.components.themes.Card;

    // The paper element uses 'overflow: hidden' to prevent card content from clipping the card's rounded corners.
    // The root element lets us add card adornments which need to overflow the Paper element and clip the card's edge.

    const root: CSSObject = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    };

    const surface: CSSObject = {
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,

        '&[data-selected]': {
            borderStyle: 'solid',
            borderColor: theme.fn.resolveColorValue(cardTheme.selectedBorderColor),
            borderWidth: `0 ${cardTheme.selectedBorderWidth}px ${cardTheme.selectedBorderWidth}px`,
        },
    };

    const sections: CSSObject = {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,

        ...createResponsiveStyles(
            [
                {
                    value: orientation,
                    getStyles: (value: CardOrientation) => {
                        return {
                            flexDirection: value === 'portrait' ? 'column' : 'row',
                        };
                    },
                },
            ],
            theme
        ),
    };

    return {
        styles: {
            root,
            surface,
            sections,
        },
    };
}
